import {
  createStyles,
  makeStyles,
  useMediaQuery,
  useTheme,
} from '@material-ui/core';
import { createSelector } from '@reduxjs/toolkit';
import React, { CSSProperties, memo } from 'react';
import AutoSizer, { Size } from 'react-virtualized-auto-sizer';
import { FixedSizeList as List, ListChildComponentProps } from 'react-window';

import { useAppSelector } from 'src/hooks/useStore';
import {
  ChannelOptionData,
  ChannelSidebarItem,
} from 'src/legacy/components/Channels/ChannelSidebarItem';
import { ChannelsSidebar } from 'src/legacy/components/Channels/ChannelsSidebar';
import { ChannelType } from 'src/legacy/components/Channels/useChannelForm';
import { InboxSidebarItemData } from 'src/legacy/components/Inbox';
import {
  LinkUrlListWrapper,
  SelectorWrapper,
} from 'src/legacy/components/ListWrappers/LinkUrlListWrapper';
import StandardPage from 'src/legacy/components/UI/StandardPage';
import { RootState } from 'src/store/reduxTypes';

export interface BaseChannelFormData {
  fields: {
    membersType: string;
    memberIDs: string[];
    companyID?: string;
  };
}

export interface ItemRenderProps<T> {
  channelOption: T;
  style?: CSSProperties;
  onSelect: (channelData: T) => void;
}

export interface CreateActionModalProps {
  open: boolean;
  onClose: () => void;
}

interface BaseChannelSidebarProps<T> {
  sidebarItemOptions: any;
  standardPageParams?: any;
  onSelectChannel: (sidebarItemOption: any) => void;
  isChannelRootSelected?: (sidebarItemOption: any) => boolean;
  shouldSetQueryParam?: boolean;
  title?: React.ReactNode;
  modalTitle?: string;
  // this will help in rendering the list with independent styling
  itemRenderer?: React.FC<ItemRenderProps<T>>;
  // if true disable the default behaviour of selecting the first channel of sidebar
  disableDefaultSelect?: boolean;
  onCreate?: (values: BaseChannelFormData) => Promise<void>;
  initialCreateFormValue?: BaseChannelFormData;
  entity: ChannelType;
  // override the default create modal
  CreateModal?: React.FC<CreateActionModalProps>;
  headerActionsSlot?: React.ReactNode;
  allowKeyboardShortcuts?: boolean;
  isTransparentSearchStyle?: boolean;
  searchPlaceholder?: string;
}

interface ItemData<T extends ChannelOptionData | InboxSidebarItemData>
  extends Pick<
    BaseChannelSidebarProps<T>,
    'isChannelRootSelected' | 'onSelectChannel' | 'sidebarItemOptions'
  > {
  channelType: ChannelType;
  ItemRenderer: BaseChannelSidebarProps<T>['itemRenderer'];
}

const useStyles = makeStyles(() =>
  createStyles({
    // the auto sizer container is a child of the drawer paper
    // which is a flex container (see SideDrawer.tsx)
    // When using an AutoSizer as a direct child of a flex box
    // it should be wrapped inside a div with flex: 1 1 auto;
    // Docs link: https://github.com/bvaughn/react-virtualized/blob/master/docs/usingAutoSizer.md#can-i-use-autosizer-within-a-flex-container
    autoSizerContainer: {
      flex: '1 1 auto',
    },
    list: {
      position: 'relative',
      '&::-webkit-scrollbar': {
        display: 'none',
      },
    },
  }),
);

const ITEM_HEIGHT_V2 = 68;

const selector = createSelector(
  [
    (state: RootState) => state.user.isClient,
    (state: RootState) => state.files.selectedChannel?.id,
    (state: RootState) => state.dashboard.selectedItemId,
  ],
  (
    isClient: boolean,
    fileSelectedChannelId: string | undefined,
    selectedExtensionItemId: string,
  ) => ({
    isClient,
    fileSelectedChannelId,
    selectedExtensionItemId,
  }),
);

// Followed this guide: https://react-window.vercel.app/#/examples/list/memoized-list-items
// when the list is expensive we need to use memoization to avoid re-rendering the list
const ListItem = memo((props: ListChildComponentProps) => {
  const { index, style, data } = props;
  const {
    channelType,
    sidebarItemOptions,
    onSelectChannel,
    selectedExtensionItemId,
    fileSelectedChannelId,
    isChannelRootSelected,
    ItemRenderer,
  } = data;

  let isSelected = false;
  switch (channelType) {
    case 'fileChannel':
      isSelected =
        fileSelectedChannelId === sidebarItemOptions[index].fileChannel.id;
      break;
    default:
      isSelected = selectedExtensionItemId === sidebarItemOptions[index].id;
  }

  // check if root channel is selected using callback prop,
  // if no callback prop then root is selected by default
  const isRootSelected = isChannelRootSelected
    ? isChannelRootSelected(sidebarItemOptions[index])
    : true;

  // if we have ItemRender component use it to render the list
  // else use the default channel sidebar item
  if (ItemRenderer) {
    return (
      <div style={style as CSSProperties}>
        <ItemRenderer
          channelOption={sidebarItemOptions[index]}
          onSelect={onSelectChannel}
          key={`${channelType}-channel-${index}`}
        />
      </div>
    );
  }
  return (
    <div style={style as CSSProperties}>
      <ChannelSidebarItem
        channelType={channelType}
        channelOption={sidebarItemOptions[index]}
        onSelect={onSelectChannel}
        isSelected={isSelected}
        isRootSelected={isRootSelected}
        key={`${channelType}-channel-${index}`}
      />
    </div>
  );
});

function getListComponent<T extends ChannelOptionData | InboxSidebarItemData>({
  classes,
  height,
  itemData,
  sidebarItemOptions,
  width,
}: {
  classes: ReturnType<typeof useStyles>;
  height: number;
  itemData: ItemData<T>;
  sidebarItemOptions: ItemData<T>['sidebarItemOptions'];
  width: number;
}) {
  return (_: SelectorWrapper, refList: React.RefObject<any>) => (
    <List
      ref={refList}
      className={classes.list}
      height={height}
      width={width}
      itemSize={ITEM_HEIGHT_V2}
      itemCount={sidebarItemOptions.length}
      itemData={itemData}
    >
      {ListItem}
    </List>
  );
}

const BaseChannelSidebar = <
  T extends ChannelOptionData | InboxSidebarItemData,
>({
  onCreate,
  initialCreateFormValue,
  entity,
  sidebarItemOptions,
  standardPageParams,
  onSelectChannel,
  isChannelRootSelected,
  shouldSetQueryParam = true,
  title = null,
  modalTitle = '',
  headerActionsSlot,
  itemRenderer: ItemRenderer,
  disableDefaultSelect = false,
  CreateModal,
  allowKeyboardShortcuts = true,
  isTransparentSearchStyle = false,
  searchPlaceholder = '',
}: BaseChannelSidebarProps<T>) => {
  const classes = useStyles();

  const theme = useTheme();
  const isSmallWidth = useMediaQuery(theme.breakpoints.down('xs'), {
    noSsr: true,
  });

  const { isClient, fileSelectedChannelId, selectedExtensionItemId } =
    useAppSelector(selector);

  const itemData: ItemData<T> = React.useMemo(
    () => ({
      channelType: entity,
      sidebarItemOptions,
      onSelectChannel,
      isChannelRootSelected,
      ItemRenderer,
      fileSelectedChannelId,
      selectedExtensionItemId,
      itemRenderer: ItemRenderer,
    }),
    [
      entity,
      sidebarItemOptions,
      onSelectChannel,
      isChannelRootSelected,
      ItemRenderer,
      fileSelectedChannelId,
      selectedExtensionItemId,
      ItemRenderer,
    ],
  );

  const sidebarContentRenderer = React.useCallback(() => {
    return (
      <div className={classes.autoSizerContainer}>
        <AutoSizer>
          {({ height, width }: Size) => (
            <LinkUrlListWrapper
              fallbackIndex={!isSmallWidth ? 0 : null}
              channelType={entity}
              onSelect={onSelectChannel}
              data={sidebarItemOptions}
              shouldSetQueryParam={shouldSetQueryParam}
              disableDefaultSelect={disableDefaultSelect}
              component={getListComponent({
                classes,
                height,
                width,
                sidebarItemOptions,
                itemData,
              })}
            />
          )}
        </AutoSizer>
      </div>
    );
  }, [entity, onSelectChannel, itemData]);

  const sidebarContent = () => {
    if (sidebarItemOptions.length > 0) {
      return sidebarContentRenderer();
    }
    if (!isClient && isSmallWidth) {
      return <StandardPage {...standardPageParams} />;
    }
    return null;
  };

  return (
    <ChannelsSidebar
      pageTitle={title}
      entity={entity}
      onCreate={onCreate}
      initialCreateFormValue={initialCreateFormValue}
      modalTitle={modalTitle}
      CreateModal={CreateModal}
      headerActionsSlot={headerActionsSlot}
      allowKeyboardShortcuts={allowKeyboardShortcuts}
      isTransparentSearchStyle={isTransparentSearchStyle}
      searchPlaceholder={searchPlaceholder}
    >
      {sidebarContent()}
    </ChannelsSidebar>
  );
};

export default BaseChannelSidebar;
